import React, { useState } from "react";
import { Link } from "react-router-dom";

import { useGetOrg } from "api/organizations/getOrg";
import { useGetScopedUsersList } from "api/users/getScopedUsersList";
import MainHeading from "components/MainHeading";
import NotFound from "components/NotFound";
import { useUser } from "hooks/useUser";

import OrgMembersTable from "../OrgMembersTable";
import OrgTable from "../OrgTable";
import PageSkeleton from "../PageSkeleton";
import styles from "./AdminSettings.module.css";
import { Button, Flex, TextInput, Group } from "@mantine/core";
import AddOrgMembersModal from "../AddOrgMembers";
import { useQueryClient } from "@tanstack/react-query";
import { PermissionButton } from "api/permission/buttonpermission";
import { useHasPermission } from "api/permission/useHasPermission";

export function AdminSettings() {
  const queryClient = useQueryClient();
  const { user } = useUser();
  const { has: canViewAudit } = useHasPermission("view_audit_logs");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // ✅ Email search states
  const [email, setEmail] = useState("");
  const [searchEmail, setSearchEmail] = useState<string | undefined>(undefined);

  // Fetch organization info (for org details)
  const {
    data: org,
    isLoading: orgLoading,
    isError: orgError,
  } = useGetOrg(user.org?._id);

  // Fetch members using scoped-list API
  const {
    data: membersResponse,
    isLoading: membersLoading,
    isError: membersError,
  } = useGetScopedUsersList(page, limit, searchEmail);

  const [opened, setOpened] = useState(false);

  const handleClose = async () => {
    setOpened(false);
    await queryClient.invalidateQueries(["scoped-users"]);
  };

  const handleSearch = () => {
    setPage(1); // ✅ reset pagination on search
    setSearchEmail(email.trim() || undefined);
  };

  const handleClear = () => {
    setEmail("");
    setSearchEmail(undefined);
    setPage(1);
  };

  if (user.role === "member") {
    return <NotFound />;
  }

  if (orgLoading || membersLoading) {
    return <PageSkeleton />;
  }

  if (orgError || !org || membersError || !membersResponse) {
    return <NotFound />;
  }

  return (
    <>
      <Flex justify="space-between" align="center" wrap="wrap" gap="md">
        <MainHeading title="Admin Settings" />
        {(canViewAudit ||
          (user.role as string | undefined) === "superadmin") && (
          <Button component={Link} to="/audit-log" variant="light">
            View Audit Log
          </Button>
        )}
      </Flex>

      <h2 className={styles.title}>Your Organization</h2>
      <OrgTable org={org} />
      <div
        style={{
          marginTop: "30px",
          backgroundColor: "white",
          padding: "6px 16px 20px 20px",
          borderRadius: "5px",
        }}
      >
        <Flex
          justify="space-between"
          align="center"
          mt="md"
          wrap="wrap"
          gap="md"
        >
          <h2 className={styles.title} style={{ margin: 0 }}>
            Organization Members
          </h2>

          <Group>
            {/* ✅ Search Box */}
            <TextInput
              placeholder="Search by email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              style={{
                maxWidth: 250,
                height: "42px", // Increased height
                minHeight: "42px", // Ensures minimum height
              }}
              styles={{
                input: {
                  height: "42px",
                  minHeight: "42px",
                },
              }}
            />
            <Button onClick={handleSearch} variant="outline">
              Search
            </Button>
            {searchEmail && (
              <Button onClick={handleClear} variant="subtle" color="gray">
                Clear
              </Button>
            )}

            <PermissionButton
              permission="organization_management.memberAdd"
              onClick={() => setOpened(true)}
            >
              Add Members
            </PermissionButton>
          </Group>
        </Flex>

        <div style={{ backgroundColor: "white" }}>
          <OrgMembersTable
            members={membersResponse.data}
            user={user}
            orgId={org._id}
            pagination={membersResponse.pagination}
            onPageChange={setPage}
            currentPage={page}
          />
        </div>
      </div>

      <AddOrgMembersModal opened={opened} onClose={handleClose} org={org} />
    </>
  );
}
